import _ from "lodash";
import { getIsNewAdminUIEnabled } from "Utils/ACLStoreUtils";

export const getAdminSettingsMenuItemsByKey = menuItems =>
  _.keyBy(menuItems, "key");

export const getAdminPageRoute = () => {
  if (getIsNewAdminUIEnabled()) return "/platform/accountSettings";
  return "/platform/administrator";
};

export const filterSidebarMenuItemsV2 = ({ menuItemsByKey, searchText }) => {
  // If no search text, return the full menu
  if (!searchText) {
    return menuItemsByKey;
  }

  // Find leaf nodes (no children) where title includes the search text and has a non-empty route
  const matchedItemKeys = _.keys(menuItemsByKey).filter(key => {
    const title = _.toLower(_.get(menuItemsByKey[key], "title", ""));
    const route = _.get(menuItemsByKey[key], "route", "");
    return _.includes(title, searchText) && !_.isEmpty(route);
  });

  // If no matches found for the search text, return an empty menu
  if (_.isEmpty(matchedItemKeys) && searchText) {
    return { root: { children: [] } };
  }

  // Find parent keys of matched items (or grandparent if parent has no route)
  const matchedParentKeys = _.compact(
    _.flatMap(_.keys(menuItemsByKey), key => {
      const item = menuItemsByKey[key];
      const children = _.get(item, "children", []);
      const hasMatchedChild = _.some(matchedItemKeys, itemKey =>
        _.includes(children, itemKey)
      );
      const route = _.get(item, "route", "");
      // We are checking if the parent has a route if not, we will include the grandparent
      // grandparent will always have a route
      if (hasMatchedChild) {
        if (!_.isEmpty(route)) {
          return key; // include this item as parent
        } else {
          const parent = _.get(item, "parent", null);
          const parentRoute = _.get(menuItemsByKey[parent], "route", "");
          if (parent && !_.isEmpty(parentRoute)) {
            return parent; // include the grandparent instead
          }
        }
      }
      return null;
    })
  );

  // Build filtered menu containing root, matched items, and matched parents
  const filteredMenu = _.pick(
    menuItemsByKey,
    _.uniq(["root", ...matchedItemKeys, ...matchedParentKeys])
  );

  const clonedMenu = _.cloneDeep(filteredMenu);

  // Find top-level items (L1) from filtered menu
  const filteredMenuWithL1LevelIds = _.keys(
    _.pickBy(clonedMenu, item => _.isEqual(item.levelId, "L1"))
  );

  // Keep only matched children in children array
  _.forEach(clonedMenu, (item, key) => {
    clonedMenu[key] = {
      ...item,
      children: _.filter(_.get(item, "children", []), childKey =>
        _.has(clonedMenu, childKey)
      ),
    };
  });

  // Update root's children to include matched top-level items and parents
  if (clonedMenu["root"]) {
    clonedMenu["root"] = {
      ...clonedMenu["root"],
      children: _.uniq([...matchedParentKeys, ...filteredMenuWithL1LevelIds]),
    };
  }

  // Set tier/listType and attach item to grandparent if immediate parent is missing
  _.forEach(clonedMenu, item => {
    if (item.levelId === "L2" || item.levelId === "L3") {
      item.tier = 0;
      _.set(item, "prefix", undefined);
      let parent = clonedMenu[item.parent];

      // If parent not found, attach to grandparent and push into its children
      if (!parent) {
        const parentKey = _.get(menuItemsByKey[item.parent], "parent", null);
        parent = clonedMenu[parentKey];
        item.parent = parentKey;

        if (parent) {
          parent.children.push(item.key);
        }
      }

      // Mark parent as slider-header
      if (parent) {
        parent.tier = 1;
        parent.listType = "slider-header";
      }
    }

    // Set subtext for level 1 items using their parent's title
    if (item.levelId === "L1") {
      const parent = menuItemsByKey[item.parent];
      item.subtext = parent.title;
      item.parent = "root";
      item.tier = 1;
    }
  });

  // Return the filtered and transformed menu
  return clonedMenu;
};

export const filterSidebarChildrenItemsV2 = ({
  menuItemsByKey,
  searchText,
  selectedOptionKey,
}) => {
  if (!searchText) return menuItemsByKey;

  const lowerSearchText = _.toLower(searchText);

  // Walk up to find the slider submenu root (the node with isSliderSubmenu: true)
  let submenuRootKey = _.get(menuItemsByKey[selectedOptionKey], "parent", "");
  let current = submenuRootKey;
  while (current && current !== "root") {
    const node = menuItemsByKey[current];
    if (_.get(node, "isSliderSubmenu")) {
      submenuRootKey = current;
      break;
    }
    current = _.get(node, "parent", null);
  }

  const submenuRoot = menuItemsByKey[submenuRootKey];
  const rootChildren = _.get(submenuRoot, "children", []);

  const clonedMenu = _.cloneDeep(menuItemsByKey);
  const keysToInclude = new Set();

  // Include all ancestors up to (but not including) submenu root
  const addAncestors = key => {
    let cur = key;
    while (cur && cur !== submenuRootKey && cur !== "root") {
      keysToInclude.add(cur);
      cur = _.get(menuItemsByKey[cur], "parent", null);
    }
  };

  // Include node and all its descendants
  const addDescendants = key => {
    keysToInclude.add(key);
    _.forEach(_.get(menuItemsByKey[key], "children", []), addDescendants);
  };

  // Bottom-up matching: only match leaf nodes, parents appear as structural
  // containers only if they have matching leaves underneath.
  const walkAndMatch = childrenKeys => {
    _.forEach(childrenKeys, childKey => {
      const child = menuItemsByKey[childKey];
      if (!child) return;
      const title = _.toLower(_.get(child, "title", ""));
      const childChildren = _.get(child, "children", []);

      if (!_.isEmpty(childChildren)) {
        // Parent node: always recurse into children
        walkAndMatch(childChildren);
      } else if (_.includes(title, lowerSearchText)) {
        // Leaf match: include this node and its ancestors
        keysToInclude.add(childKey);
        addAncestors(childKey);
      }
    });
  };

  walkAndMatch(rootChildren);

  // Filter children arrays to only include matched keys
  _.forEach(clonedMenu, (item, key) => {
    if (item.children) {
      clonedMenu[key] = {
        ...item,
        children: _.filter(item.children, c => keysToInclude.has(c)),
      };
    }
  });

  // Filter submenu root's children
  clonedMenu[submenuRootKey] = {
    ...clonedMenu[submenuRootKey],
    children: _.filter(rootChildren, c => keysToInclude.has(c)),
  };

  return clonedMenu;
};

export const getInitialNode = nodes => {
  const rootNode = _.find(nodes, { id: "root" });

  const firstChildNodeId = _.head(_.get(rootNode, "children", []));

  const childNode = _.find(nodes, { id: firstChildNodeId });

  if (!_.isEmpty(_.get(childNode, "children", []))) {
    const firstGrandChildNodeId = _.head(_.get(childNode, "children", []));
    return _.find(nodes, { id: firstGrandChildNodeId });
  }

  return childNode;
};

export const getInitialRoute = nodes => {
  return getInitialNode(nodes)?.route;
};

export const getVariablesForAdminSettings = ({
  curriculumPrograms,
  areProgramSectionsEnabled,
  isAcademicProgramConsumptionEnabled,
}) => {
  const contexts = [[]];

  const programsCount = _.size(curriculumPrograms);

  for (let i = 0; i < programsCount; i++) {
    const { id } = curriculumPrograms[i];

    const context = [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ];

    contexts.push(context);
  }

  return {
    filters: {
      module:
        areProgramSectionsEnabled || isAcademicProgramConsumptionEnabled
          ? "ACCOUNT_PORTAL"
          : "TRIMMED_ACCOUNT_PORTAL", //TBD: Create a new tag for admin settings
      appType: ["ADMIN_WEB"],
      contexts,
    },
  };
};
